import deepFull from "@/assets/images/service-deep-cleaning.webp";
import deepSm from "@/assets/images/service-deep-cleaning-700.webp";
import regularFull from "@/assets/images/service-regular-cleaning.webp";
import regularSm from "@/assets/images/service-regular-cleaning-700.webp";
import moveFull from "@/assets/images/service-move-in-move-out.webp";
import moveSm from "@/assets/images/service-move-in-move-out-700.webp";
import oneTimeFull from "@/assets/images/service-one-time-cleaning.webp";
import oneTimeSm from "@/assets/images/service-one-time-cleaning-700.webp";
import officeFull from "@/assets/images/service-office-cleaning.webp";
import officeSm from "@/assets/images/service-office-cleaning-700.webp";

export interface ServiceData {
  slug: string;
  title: string;
  subtitle: string;
  price: string;
  /** Full-resolution image (used for the service-detail hero). */
  image: string;
  /** ~700px card variant (used with `image` in a srcset on cards). */
  imageSm: string;
  description: string;
  features: string[];
  process: { step: string; desc: string }[];
}

export const services: ServiceData[] = [
  {
    slug: "deep-cleaning",
    title: "Deep Cleaning",
    subtitle: "Top-to-bottom reset for your whole home",
    price: "Free Estimate",
    image: deepFull,
    imageSm: deepSm,
    description:
      "Our most thorough service. Deep cleaning goes past the surfaces a regular clean covers — baseboards, inside appliances, grout lines, blinds, ceiling fans, door frames, and every high-touch spot that quietly builds up over the months. It's the right choice for a seasonal reset, a first-time clean before starting recurring service, or getting a Germantown home guest-ready.",
    features: [
      "Kitchen detail — inside microwave, stovetop, appliance fronts",
      "Bathroom detail — tile, grout, fixtures, glass & hard-water spots",
      "Baseboards, door frames, switch plates & vents",
      "Ceiling fans, light fixtures & window sills",
      "Full dusting, vacuuming & floor mopping throughout",
      "Ideal as a first clean before recurring service",
    ],
    process: [
      { step: "Walkthrough", desc: "We tour the home, note problem areas, pets, and any surfaces that need special care." },
      { step: "Custom Plan", desc: "You get a room-by-room deep cleaning checklist and a clear flat quote." },
      { step: "Deep Clean", desc: "Our team works top-to-bottom, room-by-room, so nothing gets skipped." },
      { step: "Final Check", desc: "We walk the home with you before we leave to make sure it's right." },
    ],
  },
  {
    slug: "regular-cleaning",
    title: "Regular Cleaning",
    subtitle: "Recurring weekly, bi-weekly & monthly service",
    price: "Free Estimate",
    image: regularFull,
    imageSm: regularSm,
    description:
      "Recurring maintenance cleaning that keeps your home consistently fresh without the weekend scramble. We follow the same checklist every visit — kitchens, bathrooms, bedrooms, living spaces, floors, and high-touch surfaces — on the schedule that fits your household. Weekly, bi-weekly, and monthly plans available across Germantown, Collierville, Cordova, and Bartlett.",
    features: [
      "Kitchens — counters, sinks, appliance exteriors & floors",
      "Bathrooms — toilets, showers, sinks, mirrors & floors",
      "Bedrooms & living areas dusted and tidied",
      "Vacuuming and mopping throughout the home",
      "High-touch surfaces wiped and sanitized",
      "Weekly, bi-weekly, or monthly scheduling",
    ],
    process: [
      { step: "Consultation", desc: "We learn your home, your priorities, and how often you want us there." },
      { step: "Set the Plan", desc: "A repeatable checklist is built around your rooms and your budget." },
      { step: "Recurring Visits", desc: "We arrive on schedule and follow the same standard every single time." },
      { step: "Stay in Touch", desc: "Adjust the plan any time — your priorities change, so can the checklist." },
    ],
  },
  {
    slug: "move-in-move-out-cleaning",
    title: "Move-In / Move-Out Cleaning",
    subtitle: "Empty-home cleaning for moves & turnovers",
    price: "Free Estimate",
    image: moveFull,
    imageSm: moveSm,
    description:
      "Moving is stressful enough. We handle the clean so you can hand over the keys with confidence — or walk into a new place that's genuinely move-in ready. We clean the spaces nobody thinks about until the home is empty: inside cabinets and drawers, inside the oven and fridge, closets, baseboards, and every corner a landlord or buyer will check.",
    features: [
      "Inside cabinets, drawers & closets",
      "Inside oven, refrigerator & appliances",
      "Full bathroom and kitchen detail clean",
      "Baseboards, door frames, switch plates & vents",
      "Floors vacuumed and mopped throughout",
      "Great for renters, sellers & property managers",
    ],
    process: [
      { step: "Confirm Scope", desc: "We confirm square footage, appliance count, and your move date." },
      { step: "Flat Quote", desc: "You get one clear price for the whole job — no hourly surprises." },
      { step: "Empty-Home Clean", desc: "We work through every room, cabinet, and appliance start to finish." },
      { step: "Ready to Hand Off", desc: "The home is left inspection-ready for your landlord, buyer, or tenant." },
    ],
  },
  {
    slug: "one-time-cleaning",
    title: "One-Time Cleaning",
    subtitle: "A single visit, no commitment",
    price: "Free Estimate",
    image: oneTimeFull,
    imageSm: oneTimeSm,
    description:
      "Sometimes you just need help once. Hosting family, recovering from a party, getting ready for photos, or finally tackling the rooms that got away from you — book a single visit with no recurring plan and no strings attached. Tell us what matters most and we'll focus our time exactly where you want it.",
    features: [
      "Book a single visit — no contract or recurring plan",
      "You choose the rooms and the priorities",
      "Perfect before or after hosting guests",
      "Great for post-party or post-project cleanup",
      "Flexible weekday scheduling",
      "Easy to convert to recurring service later",
    ],
    process: [
      { step: "Tell Us What's Needed", desc: "Share the rooms, the square footage, and what's most important to you." },
      { step: "Free Estimate", desc: "We send a clear, no-pressure price for the visit." },
      { step: "We Clean", desc: "Our team focuses on the priorities you set, room by room." },
      { step: "You Decide", desc: "Love it? Turn it into a recurring plan. If not, no obligation at all." },
    ],
  },
  {
    slug: "office-cleaning",
    title: "Office Cleaning",
    subtitle: "Clean, healthy workspaces for local businesses",
    price: "Free Estimate",
    image: officeFull,
    imageSm: officeSm,
    description:
      "A clean office is the first thing clients notice and the last thing your team should worry about. MGL Cleaning Solutions keeps Germantown and Memphis-area workspaces dusted, sanitized, and presentable — desks, breakrooms, restrooms, conference rooms, and lobbies — on a schedule that works around your business day.",
    features: [
      "Desks, workstations & private offices",
      "Conference rooms, lobbies & reception areas",
      "Breakrooms, kitchens & coffee stations",
      "Restrooms cleaned, sanitized & restocked",
      "Trash removal & high-touch disinfection",
      "Recurring plans built around your business hours",
    ],
    process: [
      { step: "Office Walkthrough", desc: "We assess square footage, headcount, and traffic patterns." },
      { step: "Cleaning Plan", desc: "A custom checklist is built around your layout and your budget." },
      { step: "Recurring Service", desc: "Weekly or bi-weekly visits scheduled around your team's day." },
      { step: "Direct Contact", desc: "One point of contact for requests, changes, and quality questions." },
    ],
  },
];
